import { connect } from 'react-redux'
import { mapProps, compose, withProps, withHandlers, lifecycle } from 'recompose'
import { getFormValues, getFormSyncErrors } from 'redux-form'
import debounce from 'lodash/debounce'
import isFunction from 'lodash/isFunction'
import { handleChooseAddressSuggestion } from './actions'
import withKeyPress from 'components/common/hoc/withKeyPress/withKeyPress'
import reduxForm from 'components/common/hoc/withFormErrorFocus'
import createLocationSearchSuggestionFormViewModel from './createLocationSearchSuggestionFormViewModel'
import withGooglePlacesSuggestions from './withGooglePlacesSuggestions'
import validate from './validate'
import { getLocationPathname } from 'selectors/getPageLocation'
import { getSearchErrors } from './selectors'
import { getApiErrorBrands } from './helpers'
import SavedLocationSearchForm from './SavedLocationSearchForm'
import { LOCATION_FINDER_INPUT, LOCATION_FINDER_FORM, DEBOUNCE_INTERVAL, STORE_LOCATOR_PAGE } from 'constant'

const DEFAULT_BUTTON_TEXT = 'search'

const withHandleOptionsFetch = withHandlers({
  handleOptionsFetch: ({ fetchAddresses }) => debounce(fetchAddresses, DEBOUNCE_INTERVAL)
})

const withResetApiErrorAfterChange = lifecycle({
  componentDidUpdate({ formValues: prevFormValues = {}} = {}) {
    const { resetFormErrorAction, formValues = {}, form, apiError } = this.props

    const prevInputValue = prevFormValues[LOCATION_FINDER_INPUT]
    const inputValue = formValues[LOCATION_FINDER_INPUT]

    const isChangedToEmpty = prevInputValue && !inputValue

    const isFormWithError = form && apiError

    if (isChangedToEmpty && isFormWithError && isFunction(resetFormErrorAction)) {
      resetFormErrorAction({ form: form || LOCATION_FINDER_FORM })
    }
  }
})

const withFormModel = mapProps(({
  addresses: addressSuggestions,
  stateOptions,
  handleSubmit,
  resetAddresses,
  botStoreLocation,
  handleBOTStoreLocationSearch,
  ...props
}) => ({
  ...props,
  handleSubmit,
  handleBOTStoreLocationSearch,
  botStoreLocation,
  enter: botStoreLocation ? handleBOTStoreLocationSearch : handleSubmit,
  model: createLocationSearchSuggestionFormViewModel({
    addressSuggestions,
    stateOptions,
    resetAddresses
  })
}))

const withLocationFormHandlers = withHandlers({
  handleFormSpecificChooseAddressSuggestion:
      ({ form, handleChooseAddressSuggestion }) => result => handleChooseAddressSuggestion(form, result)
})

const withHandleBOTStoreLocationSearch = withHandlers({
  handleBOTStoreLocationSearch: ({ onSubmit, formValues, useMyLocationClicked, setUseMyLocationClicked }) => (e) => {
    e.preventDefault()
    useMyLocationClicked && setUseMyLocationClicked(false)
    onSubmit(formValues)
  }
})

const withGetSuggestionValue = withProps({
  getSuggestionValue: ({ place }) => place
})

const withApiErrorProps = withProps(
  ({
    disableErrors,
    apiError,
    hasFormSyncErrors,
    fieldIsEmpty,
    isStoreLocatorModal,
    isStoreLocatorPage,
    submitFailed,
    useMyLocationClicked
  }) => ({
    shouldShowApiError: !disableErrors && apiError && (!hasFormSyncErrors || useMyLocationClicked) &&
        (isStoreLocatorModal || isStoreLocatorPage),
    hasBrands: apiError && getApiErrorBrands(apiError).length > 0,
    quickApptIsEmpty: apiError && fieldIsEmpty && !submitFailed
  }))

export const createSavedLocationSearchForm = ({ name = LOCATION_FINDER_FORM, buttonText } = {}) =>
  compose(
    withGooglePlacesSuggestions,
    withHandleOptionsFetch,
    connect(null, { handleChooseAddressSuggestion }),
    reduxForm({ form: name, validate }),
    connect(state => ({
      formValues: getFormValues(name)(state),
      hasFormSyncErrors: Object.keys(getFormSyncErrors(name)(state)).length > 0,
      apiError: getSearchErrors(state)[name],
      isStoreLocatorPage: getLocationPathname(state) === STORE_LOCATOR_PAGE
    })),
    withHandleBOTStoreLocationSearch,
    withLocationFormHandlers,
    withGetSuggestionValue,
    withFormModel,
    withProps(({ buttonText: propsButtonText }) => ({
      buttonText: propsButtonText || buttonText || DEFAULT_BUTTON_TEXT
    })),
    withKeyPress,
    withResetApiErrorAfterChange,
    withApiErrorProps
  )(SavedLocationSearchForm)

export default createSavedLocationSearchForm()
